import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  CAMPAIGN_JOIN_MODES,
  CAMPAIGN_TRIGGERS,
  CampaignModalSection,
  CampaignModalShell,
  CampaignModalSummary,
  CampaignOptionCard,
  CampaignSubChoice,
  NO_ORDER_MIN_AGE_DAYS,
  hasCampaignJoinRule,
} from '#/entities/campaign'
import type { CampaignJoinMode } from '#/entities/campaign'
import {
  AUDIENCE_BACKTEST_DAYS,
  CampaignScopeSection,
  CampaignTriggerFields,
  toAudienceQuery,
  useCampaignAudienceCount,
  useCampaignEnrolment,
} from '#/features/campaign-enrol'
import type { UseCampaignEnrolmentResult } from '#/features/campaign-enrol'
import type { CampaignAudienceQuery } from '#/shared/api/generated/communication/models'
import { Input } from '#/shared/ui/input'

import { useCampaignJoinFilters } from '../model/use-campaign-join-filters'
import { useCreateCampaign } from '../model/use-create-campaign'
import { CampaignFilterBuilder } from './campaign-filter-builder'

/** What the dialog's three answers ask the audience count. */
function toNewCampaignAudience(
  join: CampaignJoinMode,
  enrolment: UseCampaignEnrolmentResult,
  definition: Record<string, unknown>,
): CampaignAudienceQuery {
  return {
    ...(join === CAMPAIGN_JOIN_MODES.filter ? toAudienceQuery(definition) : {}),
    // The fourth trigger chip is a rolling filter, so it sizes as one.
    ...(join === CAMPAIGN_JOIN_MODES.trigger &&
    enrolment.trigger === CAMPAIGN_TRIGGERS.noOrder60d
      ? { min_age_days: NO_ORDER_MIN_AGE_DAYS }
      : {}),
    ...(enrolment.selectedProviderIds.length > 0
      ? { provider_ids: enrolment.selectedProviderIds }
      : {}),
    within_days: AUDIENCE_BACKTEST_DAYS,
  }
}

function NameField({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex w-full flex-col gap-1.5">
      <label
        htmlFor="campaign-name"
        className="text-2xs leading-3.5 font-medium text-warm-500"
      >
        {t('campaigns.modal.new.nameLabel')}
      </label>
      <Input
        id="campaign-name"
        value={value}
        placeholder={t('campaigns.modal.new.namePlaceholder')}
        onChange={(event) => onChange(event.target.value)}
        className="h-auto px-3.5 py-3 text-sm font-medium text-announce-ink"
      />
      <p className="text-xs leading-4 text-warm-500">
        {t('campaigns.modal.new.nameHint')}
      </p>
    </div>
  )
}

interface NewCampaignDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function NewCampaignDialog({
  open,
  onOpenChange,
}: NewCampaignDialogProps) {
  const { t } = useTranslation()
  const enrolment = useCampaignEnrolment()
  const [name, setName] = useState('')
  const [join, setJoin] = useState<CampaignJoinMode>(
    CAMPAIGN_JOIN_MODES.trigger,
  )
  const filters = useCampaignJoinFilters(join === CAMPAIGN_JOIN_MODES.filter)
  const { onCreate, isCreating } = useCreateCampaign(() => onOpenChange(false))

  // Manual enrolment has nobody to count — the people arrive one at a time
  // from the lists. Both rolling modes are replayed over a window instead of
  // counted in the present tense; a trigger campaign starts empty either way.
  const audience = useCampaignAudienceCount({
    enabled: open && join !== CAMPAIGN_JOIN_MODES.manual,
    query: toNewCampaignAudience(join, enrolment, filters.definition),
  })

  return (
    <CampaignModalShell
      open={open}
      onOpenChange={onOpenChange}
      title={t('campaigns.modal.new.title')}
      description={t('campaigns.modal.new.description')}
      submitLabel={t('campaigns.modal.new.submit')}
      submitDisabled={
        isCreating ||
        name.trim() === '' ||
        !hasCampaignJoinRule(join, filters.definition)
      }
      footerNote={t('campaigns.modal.channelNote')}
      onSubmit={() =>
        onCreate({
          name,
          scope: enrolment.scope,
          join,
          trigger: enrolment.trigger,
          filterDefinition: filters.definition,
          userIds: enrolment.appliesToUserIds,
          providerIds: enrolment.selectedProviderIds,
        })
      }
    >
      <NameField value={name} onChange={setName} />

      <CampaignScopeSection state={enrolment} showRunsForLabel />

      <CampaignModalSection label={t('campaigns.modal.join.label')}>
        <CampaignOptionCard
          title={t('campaigns.modal.join.manual.title')}
          description={t('campaigns.modal.join.manual.description')}
          selected={join === CAMPAIGN_JOIN_MODES.manual}
          onSelect={() => setJoin(CAMPAIGN_JOIN_MODES.manual)}
        />
        <CampaignOptionCard
          title={t('campaigns.modal.join.filter.title')}
          description={t('campaigns.modal.join.filter.description')}
          selected={join === CAMPAIGN_JOIN_MODES.filter}
          onSelect={() => setJoin(CAMPAIGN_JOIN_MODES.filter)}
        >
          <CampaignSubChoice label={t('campaigns.modal.join.filter.chipLabel')}>
            <CampaignFilterBuilder filters={filters} />
          </CampaignSubChoice>
        </CampaignOptionCard>
        <CampaignOptionCard
          title={t('campaigns.modal.join.trigger.title')}
          description={t('campaigns.modal.join.trigger.description')}
          selected={join === CAMPAIGN_JOIN_MODES.trigger}
          onSelect={() => setJoin(CAMPAIGN_JOIN_MODES.trigger)}
        >
          <CampaignTriggerFields state={enrolment} />
        </CampaignOptionCard>
      </CampaignModalSection>

      {/* The service heads its own figures — "Who this would have caught last
          month" for a 30-day replay, "Who this matches right now" without one —
          so the heading is read back rather than hardcoded. Manual enrolment
          has no rule to project, and keeps the design's label. */}
      <CampaignModalSummary
        label={audience.basis ?? t('campaigns.modal.new.previewLabel')}
        headline={audience.headline}
        notes={[...audience.notes, t('campaigns.modal.new.notes.skipOnly')]}
      />
    </CampaignModalShell>
  )
}
